import React, { useState, useEffect } from 'react';
import LandlordToolkitLayout from '../../components/landlord-toolkit/LandlordToolkitLayout';
import ViewingCalendar from '../../components/landlord-toolkit/ViewingCalendar';
import AddViewingModal from '../../components/landlord-toolkit/AddViewingModal';
import { Tab } from '@headlessui/react';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useAuth } from '../../auth/firebaseAuth';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { queryDocuments } from '../../utility/firebaseHelpers';
import { where } from 'firebase/firestore';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';

interface Property {
  id: number;
  title: string;
  firestoreDocId: string;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const ManageViewingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const analytics = useAnalyticsWithConsent();
  const [selectedTab, setSelectedTab] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDateForAdding, setSelectedDateForAdding] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [hasMoreViewings, setHasMoreViewings] = useState(false);
  const [lastDocId, setLastDocId] = useState<string | null>(null);

  const [calendarViewings, setCalendarViewings] = useState<any[]>([]);
  const [userProperties, setUserProperties] = useState<Property[]>([]);

  const [formData, setFormData] = useState<{
    prospectName: string;
    email: string;
    phone: string;
    propertyTitle: string;
    propertyId: string;
    time: string;
    status: 'pending' | 'confirmed' | 'attended' | 'no-show' | 'cancelled';
  }>({
    prospectName: '',
    email: '',
    phone: '',
    propertyTitle: '',
    propertyId: '',
    time: '18:00',
    status: 'pending',
  });

  // Track page view and fetch data on mount
  useEffect(() => {
    // Track page view
    analytics.trackPageView('/landlord-toolkit/manage-viewings');
    analytics.trackCustomEvent('viewing_management_page_viewed', {
      page: 'manage_viewings',
      timestamp: new Date().toISOString(),
    });

    if (currentUser) {
      fetchViewings();
      fetchUserProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchUserProperties = async () => {
    if (!currentUser) return;

    try {
      const properties = await queryDocuments('listings', [
        where('propertyOwnerUid', '==', currentUser.uid),
        where('status', '==', 'managed'),
      ]);

      setUserProperties(
        properties.map((p) => ({
          id: typeof p.id === 'number' ? p.id : Number(p.id),
          title: p.title as string,
          firestoreDocId: p.firestoreDocId as string,
        }))
      );
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  };

  const fetchViewings = async (loadMore = false) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      const idToken = await currentUser.getIdToken();

      // Build URL with pagination parameters
      const url = new URL(`${CLOUD_FUNCTIONS_BASE_URL}/getViewingsByLandlord`);
      url.searchParams.append('limit', '50'); // Fetch 50 at a time

      if (loadMore && lastDocId) {
        url.searchParams.append('lastDocId', lastDocId);
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch viewings');
      }

      const data = await response.json();

      // If loading more, append to existing viewings, otherwise replace
      if (loadMore) {
        setCalendarViewings((prev) => [...prev, ...(data.viewings || [])]);
      } else {
        setCalendarViewings(data.viewings || []);
      }

      // Update pagination state
      setHasMoreViewings(data.pagination?.hasMore || false);
      setLastDocId(data.pagination?.lastDocId || null);
    } catch (err) {
      console.error('Error fetching viewings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load viewings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddViewing = (date: Date) => {
    // Track add viewing intent
    analytics.trackInteraction('viewing_add_form_opened', {
      source: 'calendar',
      selected_date: date.toISOString(),
    });

    setSelectedDateForAdding(date);
    setShowAddForm(true);
    setShowCalendar(false);
  };

  const handleSaveViewing = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.prospectName ||
      !formData.email ||
      !formData.phone ||
      !formData.propertyTitle ||
      !formData.propertyId ||
      !selectedDateForAdding
    ) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!currentUser) {
      toast.error('You must be logged in to create a viewing');
      return;
    }

    setIsSubmitting(true);

    try {
      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/createViewing`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          propertyId: Number(formData.propertyId),
          prospectName: formData.prospectName,
          prospectEmail: formData.email,
          prospectPhone: formData.phone,
          propertyTitle: formData.propertyTitle,
          viewingDate: selectedDateForAdding.toISOString(),
          viewingTime: formData.time,
          status: formData.status,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create viewing');
      }

      await fetchViewings();

      // Track successful viewing creation
      analytics.trackCustomEvent('viewing_created', {
        property_id: formData.propertyId,
        viewing_date: selectedDateForAdding.toISOString(),
        viewing_time: formData.time,
        status: formData.status,
        timestamp: new Date().toISOString(),
      });

      analytics.trackPixelCustom('viewing_created', {
        content_name: 'Viewing Created',
        content_category: 'landlord_toolkit',
        value: 1,
      });

      // Reset form
      setFormData({
        prospectName: '',
        email: '',
        phone: '',
        propertyTitle: '',
        propertyId: '',
        time: '18:00',
        status: 'pending' as const,
      });
      setSelectedDateForAdding(null);
      setShowAddForm(false);

      // Show success and optionally reopen calendar
      toast.success('Viewing added successfully!');
    } catch (err) {
      toast.error(
        'Error creating viewing: ' + (err instanceof Error ? err.message : 'Unknown error')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async (viewingId: string, newStatus: string) => {
    if (!currentUser) {
      throw new Error('You must be logged in to update viewing status');
    }

    const idToken = await currentUser.getIdToken();

    const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/updateViewingStatus`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ viewingId, status: newStatus }),
    });

    if (!response.ok) {
      throw new Error('Failed to update viewing status');
    }

    // Track status update
    analytics.trackInteraction('viewing_status_updated', {
      viewing_id: viewingId,
      old_status: calendarViewings.find((v) => v.id === viewingId)?.status,
      new_status: newStatus,
      timestamp: new Date().toISOString(),
    });

    await fetchViewings();
  };

  const handleRemoveViewing = async (viewingId: string) => {
    if (!window.confirm('Are you sure you want to remove this viewing?')) {
      return;
    }

    if (!currentUser) {
      toast.error('You must be logged in to delete a viewing');
      return;
    }

    try {
      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/deleteViewing`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ viewingId }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete viewing');
      }

      // Track viewing deletion
      analytics.trackInteraction('viewing_deleted', {
        viewing_id: viewingId,
        timestamp: new Date().toISOString(),
      });

      await fetchViewings();
      toast.success('Viewing deleted successfully');
    } catch (err) {
      toast.error(
        'Error deleting viewing: ' + (err instanceof Error ? err.message : 'Unknown error')
      );
    }
  };

  // Computed values
  const now = new Date();
  const upcomingViewings = calendarViewings
    .filter((v) => {
      const viewingDate = v.viewingDate instanceof Date ? v.viewingDate : new Date(v.viewingDate);
      return viewingDate >= now && ['pending', 'confirmed'].includes(v.status);
    })
    .sort((a, b) => {
      const dateA = a.viewingDate instanceof Date ? a.viewingDate : new Date(a.viewingDate);
      const dateB = b.viewingDate instanceof Date ? b.viewingDate : new Date(b.viewingDate);
      return dateA.getTime() - dateB.getTime();
    })
    .slice(0, 5);

  const stats = {
    upcoming: calendarViewings.filter((v) => {
      const viewingDate = v.viewingDate instanceof Date ? v.viewingDate : new Date(v.viewingDate);
      return viewingDate >= now && ['pending', 'confirmed'].includes(v.status);
    }).length,
    totalProspects: new Set(calendarViewings.map((v) => v.prospectDetails?.email)).size,
    completed: calendarViewings.filter((v) => v.status === 'attended').length,
  };

  // Past viewings (completed or past date)
  const pastViewings = calendarViewings
    .filter((v) => {
      const viewingDate = v.viewingDate instanceof Date ? v.viewingDate : new Date(v.viewingDate);
      return viewingDate < now || ['attended', 'no-show', 'cancelled'].includes(v.status);
    })
    .sort((a, b) => {
      const dateA = a.viewingDate instanceof Date ? a.viewingDate : new Date(a.viewingDate);
      const dateB = b.viewingDate instanceof Date ? b.viewingDate : new Date(b.viewingDate);
      return dateB.getTime() - dateA.getTime(); // Most recent first
    });

  // Pagination calculations
  const totalPages = Math.ceil(pastViewings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPastViewings = pastViewings.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    analytics.trackInteraction('past_viewings_page_changed', {
      page,
      total_pages: totalPages,
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <LandlordToolkitLayout>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Viewing Management</h1>
        <p className="text-gray-500 mt-1">Manage your property viewings and availability.</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {!currentUser && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700">
          Please log in to manage your viewings.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
        </div>
      ) : (
        <>
          <Tab.Group
            onChange={(index) => {
              setSelectedTab(index);
              const tabNames = ['dashboard', 'past_viewings'];
              analytics.trackInteraction('viewing_tab_changed', {
                tab: tabNames[index],
                tab_index: index,
                timestamp: new Date().toISOString(),
              });
            }}
          >
            <Tab.List className="flex space-x-1 rounded-xl bg-purple-900/20 p-1 mb-8 max-w-md">
              <Tab
                className={({ selected }) =>
                  classNames(
                    'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-purple-400 focus:outline-none focus:ring-2',
                    selected
                      ? 'bg-white text-purple-700 shadow'
                      : 'text-purple-900 hover:bg-white/[0.12] hover:text-purple-800'
                  )
                }
              >
                Dashboard
              </Tab>
              <Tab
                className={({ selected }) =>
                  classNames(
                    'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                    'ring-white ring-opacity-60 ring-offset-2 ring-offset-purple-400 focus:outline-none focus:ring-2',
                    selected
                      ? 'bg-white text-purple-700 shadow'
                      : 'text-purple-900 hover:bg-white/[0.12] hover:text-purple-800'
                  )
                }
              >
                Past Viewings
              </Tab>
            </Tab.List>

            <Tab.Panels>
              {/* Dashboard Panel */}
              <Tab.Panel>
                <div className="grid gap-6">
                  {/* Stats Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-purple-100 text-purple-600 rounded-lg">
                          <CalendarIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Upcoming</p>
                          <p className="text-2xl font-bold text-gray-900">{stats.upcoming}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                          <UserGroupIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Prospects</p>
                          <p className="text-2xl font-bold text-gray-900">{stats.totalProspects}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                          <CheckCircleIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Completed</p>
                          <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Upcoming List */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                      <h2 className="text-lg font-bold text-gray-900">Upcoming Viewings</h2>
                      <button
                        onClick={() => {
                          analytics.trackInteraction('view_calendar_clicked', {
                            source: 'dashboard',
                            timestamp: new Date().toISOString(),
                          });
                          setShowCalendar(true);
                        }}
                        className="text-sm text-purple-600 font-medium hover:text-purple-700 flex items-center gap-2"
                      >
                        <CalendarIcon className="w-4 h-4" />
                        View Calendar
                      </button>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {upcomingViewings.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                          <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>No upcoming viewings scheduled</p>
                          <button
                            onClick={() => setShowCalendar(true)}
                            className="mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Add your first viewing
                          </button>
                        </div>
                      ) : (
                        upcomingViewings.map((viewing) => {
                          const viewingDate =
                            viewing.viewingDate instanceof Date
                              ? viewing.viewingDate
                              : new Date(viewing.viewingDate);
                          return (
                            <div
                              key={viewing.id}
                              className="p-6 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div className="flex items-start gap-4">
                                  <div className="mt-1 p-2 bg-gray-100 rounded-lg text-gray-500">
                                    <UserGroupIcon className="w-5 h-5" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-gray-900">
                                      {viewing.prospectDetails?.name || 'Unknown'}
                                    </h3>
                                    <p className="text-sm text-gray-500">{viewing.propertyTitle}</p>
                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                      <div className="flex items-center gap-1">
                                        <CalendarIcon className="w-4 h-4" />
                                        {format(viewingDate, 'MMM d, yyyy')} at{' '}
                                        {viewing.viewingTime}
                                      </div>
                                      <div
                                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                          viewing.status === 'confirmed'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                        }`}
                                      >
                                        {viewing.status}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <a
                                    href={`mailto:${viewing.prospectDetails?.email}`}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                                    title="Contact"
                                  >
                                    <EnvelopeIcon className="w-5 h-5" />
                                  </a>
                                  <button
                                    onClick={() => {
                                      toast.promise(handleUpdateStatus(viewing.id, 'cancelled'), {
                                        loading: 'Cancelling viewing...',
                                        success: 'Viewing cancelled successfully',
                                        error: 'Failed to cancel viewing',
                                      });
                                    }}
                                    className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                    title="Cancel"
                                  >
                                    <XCircleIcon className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const newStatus =
                                        viewing.status === 'pending' ? 'confirmed' : 'attended';
                                      const messages = {
                                        loading:
                                          viewing.status === 'pending'
                                            ? 'Confirming viewing...'
                                            : 'Marking as attended...',
                                        success:
                                          viewing.status === 'pending'
                                            ? 'Viewing confirmed successfully'
                                            : 'Viewing marked as attended',
                                        error: 'Failed to update viewing status',
                                      };
                                      toast.promise(
                                        handleUpdateStatus(viewing.id, newStatus),
                                        messages
                                      );
                                    }}
                                    className="p-2 text-green-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                                    title={
                                      viewing.status === 'pending' ? 'Confirm' : 'Mark Attended'
                                    }
                                  >
                                    <CheckCircleIcon className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </Tab.Panel>

              {/* Past Viewings Tab */}
              <Tab.Panel>
                {pastViewings.length === 0 ? (
                  <div className="bg-white p-12 rounded-xl border border-gray-200 text-center text-gray-500">
                    <ClockIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No past viewings found.</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date & Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Prospect
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Property
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contact
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedPastViewings.map((viewing) => {
                              const viewingDate =
                                viewing.viewingDate instanceof Date
                                  ? viewing.viewingDate
                                  : new Date(viewing.viewingDate);

                              const statusColors = {
                                attended: 'bg-green-100 text-green-800',
                                'no-show': 'bg-red-100 text-red-800',
                                cancelled: 'bg-gray-100 text-gray-800',
                                pending: 'bg-yellow-100 text-yellow-800',
                                confirmed: 'bg-blue-100 text-blue-800',
                              };

                              return (
                                <tr key={viewing.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                      <CalendarIcon className="w-4 h-4 text-gray-400" />
                                      <div>
                                        <div className="font-medium">
                                          {format(viewingDate, 'MMM d, yyyy')}
                                        </div>
                                        <div className="text-gray-500">{viewing.viewingTime}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2">
                                      <UserGroupIcon className="w-4 h-4 text-gray-400" />
                                      <div className="text-sm">
                                        <div className="font-medium text-gray-900">
                                          {viewing.prospectDetails?.name || 'Unknown'}
                                        </div>
                                        <div className="text-gray-500">
                                          {viewing.prospectDetails?.email}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900 max-w-xs truncate">
                                      {viewing.propertyTitle}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        statusColors[viewing.status as keyof typeof statusColors] ||
                                        'bg-gray-100 text-gray-800'
                                      }`}
                                    >
                                      {viewing.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <div className="flex items-center gap-2">
                                      <a
                                        href={`mailto:${viewing.prospectDetails?.email}`}
                                        className="text-purple-600 hover:text-purple-700"
                                        title="Email"
                                      >
                                        <EnvelopeIcon className="w-5 h-5" />
                                      </a>
                                      {viewing.prospectDetails?.phone && (
                                        <a
                                          href={`tel:${viewing.prospectDetails.phone}`}
                                          className="text-purple-600 hover:text-purple-700"
                                          title="Call"
                                        >
                                          <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                            />
                                          </svg>
                                        </a>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Load More Button */}
                    {hasMoreViewings && (
                      <div className="mt-6 flex justify-center">
                        <button
                          onClick={() => fetchViewings(true)}
                          disabled={loading}
                          className="px-6 py-3 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {loading ? 'Loading...' : 'Load More Viewings'}
                        </button>
                      </div>
                    )}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-between bg-white px-6 py-4 rounded-lg border border-gray-200">
                        <div className="text-sm text-gray-700">
                          Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                          <span className="font-medium">
                            {Math.min(endIndex, pastViewings.length)}
                          </span>{' '}
                          of <span className="font-medium">{pastViewings.length}</span> results
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <div className="flex gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg ${
                                  currentPage === page
                                    ? 'bg-purple-600 text-white'
                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {page}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </>
      )}

      {/* Calendar Modal */}
      {showCalendar && (
        <ViewingCalendar
          viewings={calendarViewings.map((v) => ({
            id: v.id,
            propertyTitle: v.propertyTitle,
            prospectName: v.prospectDetails?.name || '',
            time: v.viewingTime || '',
            status: v.status as any,
            email: v.prospectDetails?.email || '',
            phone: v.prospectDetails?.phone || '',
            date: v.viewingDate instanceof Date ? v.viewingDate : new Date(v.viewingDate),
          }))}
          onClose={() => setShowCalendar(false)}
          onAddViewing={handleAddViewing}
          onRemoveViewing={handleRemoveViewing}
          onUpdateStatus={handleUpdateStatus}
          isModal={true}
        />
      )}

      {/* Add Viewing Form Modal */}
      <AddViewingModal
        isOpen={showAddForm}
        selectedDate={selectedDateForAdding}
        formData={formData}
        userProperties={userProperties}
        isSubmitting={isSubmitting}
        onClose={() => {
          setShowAddForm(false);
          setSelectedDateForAdding(null);
        }}
        onSubmit={handleSaveViewing}
        onFormDataChange={setFormData}
      />
    </LandlordToolkitLayout>
  );
};

export default ManageViewingsPage;
